import { __assign, __awaiter, __generator } from "tslib";
import * as uuid from 'uuid';
import { w, g } from '@tinkoff/statist-page-data';
import { idCookies } from '../constants/cookieList';
import { addMessageListener, getMessageListeners } from '../statist-queue/message-listener';
import { addMessageToHistory, getMessagesHistory } from '../statist-queue/history';
import { getUtmMarkers } from '../resolvers/utmMarkers';
import { getPageUrl } from '../resolvers/pageUrl';
import { ReferrerResolver } from '../resolvers/referrer';
import { applyQueueHandler } from '../statist-queue/statist-queue';
import { EventEmitter } from '../utils/event-emitter';
import { getParams, wuid } from '../utils';
import { SDK_VERSION, STATIST_ENDPOINTS } from '../constants/environment';
import { Transport } from '../transports';
import { DEFAULT_EVENTS_JSON_SIZE_LIMIT } from '../constants/settings';
import { cookies } from '../storages/cookies';
var logError = function (error) {
    console.error(error);
};
var StatistBrowserClient = /** @class */ (function () {
    function StatistBrowserClient(clientParameters, transport, eventParameters, sessionTimeout, getClientParametersOverride) {
        if (sessionTimeout === void 0) { sessionTimeout = 30 * 60 * 1000; }
        this.clientParameters = clientParameters;
        this.transport = transport;
        this.eventParameters = eventParameters;
        this.sessionTimeout = sessionTimeout;
        this.getClientParametersOverride = getClientParametersOverride;
        this.deviceId = wuid.get();
        this.screenWidth = w.screen.width;
        this.screenHeight = w.screen.height;
        this.codegenVersion = '';
        this.eventEmitter = new EventEmitter();
        this.referrerResolver = new ReferrerResolver();
        this.eventEmitter.on('message', function (message) {
            addMessageToHistory(message);
            getMessageListeners().forEach(function (listener) {
                // try/catch - что бы при ошибке одного слушателя - все остальные отработали
                try {
                    listener(message);
                }
                catch (error) {
                    logError(error);
                }
            });
        });
        var processStatistQueue = this.processStatistQueue.bind(this);
        applyQueueHandler(processStatistQueue);
    }
    StatistBrowserClient.prototype.processStatistQueue = function (queueItem) {
        // try/catch - для корректной работы в синхронном коде
        try {
            if (!queueItem || !Array.isArray(queueItem)) {
                return;
            }
            var command = queueItem;
            switch (command[0]) {
                case 'getMessagesHistory': {
                    var _ = command[0], historyHandler = command[1];
                    historyHandler(getMessagesHistory());
                    return;
                }
                case 'onMessage': {
                    var _ = command[0], messageListener = command[1];
                    addMessageListener(messageListener);
                }
            }
        }
        catch (error) {
            logError(error);
        }
    };
    StatistBrowserClient.createTransport = function (_a) {
        var apiKey = _a.apiKey, transportOptions = _a.transportOptions, environment = _a.environment;
        var _b = transportOptions || {}, _c = _b.batchSize, batchSize = _c === void 0 ? 100 : _c, _d = _b.pushInterval, pushInterval = _d === void 0 ? 5000 : _d, _e = _b.pushTimeout, pushTimeout = _e === void 0 ? 30000 : _e, _f = _b.savedMessagesSize, savedMessagesSize = _f === void 0 ? 1000 : _f, _g = _b.eventsJsonSizeLimit, eventsJsonSizeLimit = _g === void 0 ? DEFAULT_EVENTS_JSON_SIZE_LIMIT : _g, _h = _b.endpointUrl, endpointUrl = _h === void 0 ? STATIST_ENDPOINTS[environment || 'prod'] : _h, _j = _b.gzip, gzip = _j === void 0 ? true : _j;
        var statistTransports = g._statistTransports || (g._statistTransports = {});
        // TODO: удалить при мажорном релизе
        // Если транспорт был инициализирован через старый SDK
        // То важно учесть этот созданный транспорт и не создавать новый
        if (g._statistTransport && !statistTransports[STATIST_ENDPOINTS.prod]) {
            statistTransports[STATIST_ENDPOINTS.prod] = g._statistTransport;
        }
        if (g._statistDevTransport && !statistTransports[STATIST_ENDPOINTS.test]) {
            statistTransports[STATIST_ENDPOINTS.test] = g._statistDevTransport;
        }
        var existingTransport = statistTransports[endpointUrl];
        if (existingTransport) {
            return existingTransport;
        }
        var transport = new Transport(endpointUrl, {
            apiKey: apiKey,
            batchSize: batchSize,
            pushInterval: pushInterval,
            pushTimeout: pushTimeout,
            preconnect: false,
            savedMessagesSize: savedMessagesSize,
            eventsJsonSizeLimit: eventsJsonSizeLimit,
            gzip: gzip,
        });
        statistTransports[endpointUrl] = transport;
        // TODO: удалить при мажорном релизе
        // Если мы создаем новый транспорт, то нужно его сложить в старый ключ в window,
        // чтобы он мог быть использован старыми версиями SDK
        if (endpointUrl === STATIST_ENDPOINTS.prod) {
            g._statistTransport = transport;
        }
        else if (endpointUrl === STATIST_ENDPOINTS.test) {
            g._statistDevTransport = transport;
        }
        return transport;
    };
    StatistBrowserClient.prototype.getSessionId = function () {
        var previousSessionId = cookies.get(idCookies.sessionId.name);
        var lastEventTime = Number(cookies.get(idCookies.lastEventTime.name)) || null;
        if (previousSessionId !== null &&
            lastEventTime !== null &&
            Date.now() - lastEventTime < this.sessionTimeout) {
            return previousSessionId;
        }
        var newSessionId = uuid.v7();
        cookies.set(idCookies.sessionId, newSessionId);
        cookies.remove(idCookies.lastEventTime.name, true);
        cookies.remove(idCookies.sessionStartTime.name, true);
        return newSessionId;
    };
    StatistBrowserClient.prototype.getSessionStartTime = function () {
        return cookies.get(idCookies.sessionStartTime.name) === null
            ? Date.now()
            : Number(cookies.get(idCookies.sessionStartTime.name));
    };
    StatistBrowserClient.prototype.resolveClientParameters = function (overrideClientParameters) {
        var _a;
        var clientParameters = __assign(__assign(__assign({}, (overrideClientParameters !== null && overrideClientParameters !== void 0 ? overrideClientParameters : this.clientParameters)), { sessionId: this.getSessionId(), deviceId: this.deviceId, sessionStartTime: this.getSessionStartTime(), screenWidth: this.screenWidth, screenHeight: this.screenHeight, sdkVersion: SDK_VERSION, codegenVersion: this.codegenVersion }), (_a = this.getClientParametersOverride) === null || _a === void 0 ? void 0 : _a.call(this));
        return clientParameters;
    };
    StatistBrowserClient.prototype.resolveFullClientParameters = function (overrideClientParameters) {
        var referrerValue = this.referrerResolver.resolve();
        var clientParameters = __assign(__assign(__assign(__assign({}, this.resolveClientParameters(overrideClientParameters)), this.getUtmMarkers()), (typeof referrerValue !== 'undefined' && { referrer: referrerValue })), { pageUrl: getPageUrl() });
        return clientParameters;
    };
    StatistBrowserClient.prototype.transportSend = function (message) {
        this.transport.send(message);
        cookies.set(idCookies.lastEventTime, Date.now());
        if (cookies.get(idCookies.sessionStartTime.name) === null) {
            cookies.set(idCookies.sessionStartTime, message.clientParameters.sessionStartTime);
        }
    };
    StatistBrowserClient.prototype.sendUserProperties = function (project, operation, group, values) {
        var _a, _b;
        var userProperties = (_a = {
                kind: 'userProperties',
                project: project,
                eventParameters: getParams(this.eventParameters),
                clientParameters: this.resolveClientParameters()
            },
            _a[group] = (_b = {},
                _b["$".concat(operation)] = values,
                _b),
            _a);
        this.transportSend(userProperties);
        this.eventEmitter.emit('message', { type: 'userProperties', message: userProperties });
    };
    StatistBrowserClient.prototype.send = function (project, eventName, parameters, overrideClientParameters) {
        var event = {
            project: project,
            name: eventName,
            parameters: parameters,
            eventParameters: getParams(this.eventParameters),
            clientParameters: this.resolveFullClientParameters(overrideClientParameters),
        };
        this.transportSend(event);
        this.eventEmitter.emit('message', { type: 'event', message: event });
    };
    StatistBrowserClient.prototype.updateClientParameters = function (patchClientParameters) {
        for (var key in patchClientParameters) {
            this.clientParameters[key] = patchClientParameters[key];
        }
    };
    StatistBrowserClient.prototype.updateUserProperties = function (project, operation, values) {
        this.sendUserProperties(project, operation, 'userProperties', values !== null && values !== void 0 ? values : {});
    };
    StatistBrowserClient.prototype.updateFeatureFlags = function (project, values) {
        this.sendUserProperties(project, 'set', 'featureFlags', values !== null && values !== void 0 ? values : {});
    };
    StatistBrowserClient.prototype.updateABGroups = function (project, values) {
        this.sendUserProperties(project, 'set', 'ABGroups', values !== null && values !== void 0 ? values : {});
    };
    StatistBrowserClient.prototype.on = function (eventName, handler) {
        if (eventName === 'message') {
            this.eventEmitter.on(eventName, handler);
        }
        else {
            this.transport.on(eventName, handler);
        }
    };
    StatistBrowserClient.prototype.off = function (eventName, handler) {
        if (eventName === 'message') {
            this.eventEmitter.off(eventName, handler);
        }
        else {
            this.transport.off(eventName, handler);
        }
    };
    StatistBrowserClient.prototype.forceSend = function () {
        var _a, _b;
        (_b = (_a = this.transport).forceSend) === null || _b === void 0 ? void 0 : _b.call(_a);
    };
    StatistBrowserClient.prototype.fork = function (_a) {
        var _b = _a === void 0 ? {} : _a, enrich = _b.enrich;
        // Создается новый объект, в прототип которого записывается текущий инстанс
        var fork = Object.create(this);
        if (enrich) {
            var send_1 = this.send.bind(this);
            fork.send = function (project, name, parameters, overrideClientParameters) {
                var resultParameters = parameters;
                var enrichResult = enrich
                    ? enrich({
                        project: project,
                        name: name,
                        parameters: parameters,
                        overrideClientParameters: overrideClientParameters,
                    })
                    : {};
                if (enrichResult && typeof enrichResult === 'object') {
                    resultParameters = Object.assign(enrichResult, parameters);
                }
                else {
                    logError('Statist: enrichment is corrupted - enrichment result must be object');
                }
                return send_1(project, name, resultParameters, overrideClientParameters);
            };
        }
        return fork;
    };
    StatistBrowserClient.prototype.getUtmMarkers = function () {
        return getUtmMarkers();
    };
    StatistBrowserClient.prototype.getDeviceId = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.deviceId];
            });
        });
    };
    // Используется в генераторе
    StatistBrowserClient.prototype._setCodegenVersion = function (codegenVersion) {
        this.codegenVersion = codegenVersion;
    };
    return StatistBrowserClient;
}());
export { StatistBrowserClient };
//# sourceMappingURL=statist-browser-client.js.map